import { nanoid } from 'nanoid';
import pick from 'lodash/pick.js';
import find from 'lodash/find.js';
import uniq from 'lodash/uniq.js';

export const from = 5;
export const to = 6;

/**
 * CONSTANTS
 */
const fieldsToPersist = ['name', 'ratio', 'styles'];

/**
 * Maps mediaType values to new layer type values
 */
const layerTypes = {
  animation: 'text',
  audio: 'audio',
  image: 'video',
  text: 'text',
  video: 'video',
};

export function up(schema) {
  const allItems = [
    ...schema.audio, // Audio layers
    ...schema.video, // Video layer
    ...schema.image, // Background image layer
    ...schema.foreground, // Foreground image layer
    ...schema.text, // Text layer
    ...schema.animation, // Animations/Overlays layer
  ];

  const fromV5Storyboard = pick(schema, fieldsToPersist);

  // Normalized for createEntityAdapter
  const v6Storyboard = allItems.reduce(addItemToStoryboard, {
    items: { entities: {}, ids: [] },
    layers: { entities: {}, ids: [] },
  });

  const customFonts = normalizeCustomFonts(schema.customFonts);

  return {
    ...fromV5Storyboard,
    ...v6Storyboard,
    customFonts,
    version: to,
  };
}

export function down(schema) {
  const items = schema.items.ids.map((id) => schema.items.entities[id]);

  const v5Storyboard = items.reduce(
    (storyboard, item) => {
      const { v5Layer, ...rest } = item;
      const v5Item = { ...rest, layer: v5Layer };

      if (item.mediaType === 'animation') {
        storyboard.animation.push(v5Item);
      } else if (item.mediaType === 'text') {
        storyboard.text.push(v5Item);
      } else if (item.mediaType === 'audio') {
        storyboard.audio.push(v5Item);
      } else if (item.mediaType === 'video') {
        storyboard.video.push(v5Item);
      } else if (item.mediaType === 'image') {
        if (v5Layer === 2) {
          storyboard.image.push(v5Item);
        } else {
          storyboard.foreground.push(v5Item);
        }
      }

      return storyboard;
    },
    {
      animation: [],
      audio: [],
      image: [],
      foreground: [],
      text: [],
      video: [],
    }
  );

  const fromV6Storyboard = pick(schema, fieldsToPersist);

  return {
    ...fromV6Storyboard,
    ...v5Storyboard,
    version: from,
  };
}

/**
 * Impure function that mutates the storyboard object.
 *
 * This function adds the item onto the storyboard.items object in a
 * normalized fashion and preserves the previous layer value to
 * support the down migration.
 *
 * @param {object} storyboard
 * @param {object} itemToAdd
 * @returns object
 */
function addItemToStoryboard(storyboard, itemToAdd) {
  const { items } = storyboard;

  const { id } = itemToAdd;
  items.entities[id] = { ...itemToAdd, v5Layer: itemToAdd.layer };
  items.ids.push(id);

  addToLayer(storyboard, itemToAdd);

  return storyboard;
}

function itemsOverlap(firstItem, secondItem) {
  const firstStart = firstItem.startTime;
  const firstEnd = firstItem.startTime + firstItem.duration;
  const secondStart = secondItem.startTime;
  const secondEnd = secondItem.startTime + secondItem.duration;

  return !(firstEnd <= secondStart || firstStart >= secondEnd);
}

function addToLayer(storyboard, itemToAdd) {
  const { items, layers } = storyboard;

  const layerType = layerTypes[itemToAdd.mediaType];
  const allLayers = layers.ids.map((layerId) => layers.entities[layerId]);
  const lastLayerOfType = find(allLayers, { type: layerType }); // newest layers at beginning of array

  if (!lastLayerOfType) {
    return createNewLayerAndAddItem(storyboard, itemToAdd);
  }

  const layerItems = lastLayerOfType.itemIds.map(
    (itemId) => items.entities[itemId]
  );
  const canAddToLayer = layerItems.every(
    (layerItem) => !itemsOverlap(layerItem, itemToAdd)
  );

  if (canAddToLayer) {
    layers.entities[lastLayerOfType.id].itemIds.push(itemToAdd.id);
    return storyboard;
  } else {
    return createNewLayerAndAddItem(storyboard, itemToAdd);
  }
}

/**
 * Impure function that mutates the storyboard object.
 *
 * This function creates a new layer with the passed item.
 *
 * @param {object} storyboard
 * @param {object} item
 * @returns object
 */
function createNewLayerAndAddItem(storyboard, item) {
  const { layers } = storyboard;

  const newLayerId = nanoid();
  const layerType = layerTypes[item.mediaType];
  const newLayer = {
    id: newLayerId,
    type: layerType,
    itemIds: [item.id],
  };

  layers.entities[newLayerId] = newLayer;
  layers.ids.unshift(newLayerId);

  return storyboard;
}

function normalizeCustomFonts(customFonts = []) {
  return customFonts.reduce(
    (result, font) => {
      const { usedBy, ...rest } = font;
      if (result.ids.includes(font.name)) {
        let { itemIds } = result.entities[font.name];
        itemIds = uniq([...itemIds, ...usedBy]);
      } else {
        result.ids.push(font.name);
        result.entities[font.name] = { ...rest, itemIds: uniq(usedBy) };
      }
      return result;
    },
    { ids: [], entities: {} }
  );
}

export default {
  from,
  to,
  up,
  down,
};
